import { extractAccessToken } from "./authTokenResponse";
import { getDeviceId, withDeviceHeader } from "./deviceIdentity";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
const ACCESS_MAX_AGE = 15 * 60; // 15분 (초)

export type AuthSessionReconcileStatus =
  | "authenticated"
  | "unauthenticated"
  | "unavailable";

type AccessTokenRefreshResult =
  | { status: "authenticated"; token: string }
  | { status: "unauthenticated" }
  | { status: "unavailable" };

let refreshRequest: Promise<AccessTokenRefreshResult> | undefined;
let sessionResumeGate: Promise<void> | null = null;
let releaseSessionResumeGate: (() => void) | null = null;

/**
 * history/bfcache resume 중에는 이미 mount된 화면 상태를 보존하되,
 * 새로운 authenticated request만 잠시 대기시킨다. session reconciliation은
 * raw fetch를 사용하므로 이 gate와 서로 기다리는 deadlock이 생기지 않는다.
 */
export function beginAuthSessionResumeGate() {
  if (sessionResumeGate) return;
  sessionResumeGate = new Promise<void>((resolve) => {
    releaseSessionResumeGate = resolve;
  });
}

export function releaseAuthSessionResumeGate() {
  releaseSessionResumeGate?.();
  releaseSessionResumeGate = null;
  sessionResumeGate = null;
}

async function waitForAuthSessionResumeGate() {
  if (sessionResumeGate) await sessionResumeGate;
}

function getAccessToken(): string | undefined {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith("seexr_access="))
    ?.split("=")[1];
}

function setAccessToken(token: string) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `seexr_access=${token}; path=/; max-age=${ACCESS_MAX_AGE}; samesite=lax${secure}`;
}

function clearAccessToken() {
  document.cookie = "seexr_access=; Max-Age=0; path=/";
}

function getCurrentPathWithSearch(): string {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}

async function performAccessTokenRefresh(): Promise<AccessTokenRefreshResult> {
  try {
    const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
      method: "POST",
      credentials: "include", // refreshToken/seexr_refresh 쿠키 자동 전송
      headers: withDeviceHeader(),
    });

    if (!refreshRes.ok) {
      // 네트워크/서버 장애와 달리 인증 거부는 현재 세션이 유효하지 않다는
      // 명시적인 신호이므로 이때만 클라이언트 access cookie를 제거한다.
      if (refreshRes.status === 401 || refreshRes.status === 403) {
        clearAccessToken();
        return { status: "unauthenticated" };
      }
      return { status: "unavailable" };
    }

    const data: unknown = await refreshRes.json().catch(() => undefined);
    const accessToken = extractAccessToken(data) ?? getAccessToken();
    if (!accessToken) return { status: "unavailable" };

    setAccessToken(accessToken);
    return { status: "authenticated", token: accessToken };
  } catch {
    // 성공 응답의 Set-Cookie가 이미 적용됐을 수 있으므로, 응답 파싱이나
    // 일시적인 네트워크 실패만으로 기존/갱신된 쿠키를 지우지 않는다.
    return { status: "unavailable" };
  }
}

function refreshAccessTokenResult(): Promise<AccessTokenRefreshResult> {
  if (!refreshRequest) {
    refreshRequest = performAccessTokenRefresh().finally(() => {
      refreshRequest = undefined;
    });
  }

  return refreshRequest;
}

async function refreshAccessToken(): Promise<string | undefined> {
  const result = await refreshAccessTokenResult();
  return result.status === "authenticated" ? result.token : undefined;
}

export async function ensureAccessToken(): Promise<string | undefined> {
  await waitForAuthSessionResumeGate();
  return getAccessToken() ?? refreshAccessToken();
}

async function fetchCurrentUser(token: string | undefined): Promise<Response> {
  const headers = withDeviceHeader();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetchWithSafeNetworkRetry(
    `${API_URL}/api/users/me`,
    {
      headers,
      credentials: "include",
    },
    2,
  );
}

/**
 * history/bfcache resume 전에 현재 브라우저 세션을 한 번만 정리한다.
 * 401/403은 실제 인증 만료로, 네트워크/5xx는 unavailable로 구분하여
 * 일시 장애 때문에 기존 로그인 상태를 지우거나 로그인 화면으로 보내지 않는다.
 */
export async function reconcileAuthSession(): Promise<AuthSessionReconcileStatus> {
  const token = getAccessToken();

  try {
    const response = await fetchCurrentUser(token);
    if (response.ok) return "authenticated";
    if (response.status !== 401 && response.status !== 403) return "unavailable";
  } catch {
    return "unavailable";
  }

  const latestToken = getAccessToken();
  const refreshResult =
    latestToken && latestToken !== token
      ? { status: "authenticated" as const, token: latestToken }
      : await refreshAccessTokenResult();

  if (refreshResult.status !== "authenticated") return refreshResult.status;

  try {
    const retryResponse = await fetchCurrentUser(refreshResult.token);
    if (retryResponse.ok) return "authenticated";
    if (retryResponse.status === 401 || retryResponse.status === 403) {
      clearAccessToken();
      return "unauthenticated";
    }
    return "unavailable";
  } catch {
    return "unavailable";
  }
}

export interface FetchWithAuthOptions extends RequestInit {
  skipRedirectOn401?: boolean;
  /**
   * GET/HEAD 요청의 일시적인 네트워크 실패 재시도 횟수.
   * 변경 요청은 명시해도 재시도하지 않아 중복 mutation을 만들지 않는다.
   */
  networkRetryCount?: number;
}

export type AuthUploadProgress = {
  loaded: number;
  total: number;
  percent: number;
};

export type UploadWithAuthProgressOptions = {
  signal?: AbortSignal;
  skipRedirectOn401?: boolean;
  onProgress?: (progress: AuthUploadProgress) => void;
};

function xhrResponse(xhr: XMLHttpRequest): Response {
  const headers = new Headers();
  for (const line of xhr
    .getAllResponseHeaders()
    .trim()
    .split(/[\r\n]+/)) {
    if (!line) continue;
    const separatorIndex = line.indexOf(":");
    if (separatorIndex < 0) continue;
    headers.append(
      line.slice(0, separatorIndex).trim(),
      line.slice(separatorIndex + 1).trim(),
    );
  }
  return new Response(xhr.responseText, {
    status: xhr.status,
    statusText: xhr.statusText,
    headers,
  });
}

function sendAuthenticatedUpload(
  url: string,
  body: XMLHttpRequestBodyInit,
  token: string | undefined,
  options: UploadWithAuthProgressOptions,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException("업로드가 취소되었습니다.", "AbortError"));
      return;
    }

    const xhr = new XMLHttpRequest();
    let settled = false;
    let lastUploadTotal = 0;
    let lastUploadPercent = -1;
    const cleanup = () =>
      options.signal?.removeEventListener("abort", handleSignalAbort);
    const rejectOnce = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const handleSignalAbort = () => xhr.abort();

    xhr.open("POST", url, true);
    xhr.withCredentials = true;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    const deviceId = getDeviceId();
    if (deviceId) xhr.setRequestHeader("X-SEEXR-Device-ID", deviceId);
    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return;
      lastUploadTotal = event.total;
      lastUploadPercent = Math.min(
        100,
        Math.round((event.loaded / event.total) * 100),
      );
      options.onProgress?.({
        loaded: event.loaded,
        total: event.total,
        percent: lastUploadPercent,
      });
    };
    xhr.upload.onload = () => {
      if (lastUploadPercent >= 100) return;
      const total = lastUploadTotal || 1;
      options.onProgress?.({ loaded: total, total, percent: 100 });
    };
    xhr.onload = () => {
      if (settled) return;
      if (xhr.status === 0) {
        rejectOnce(new Error("네트워크 연결을 확인해주세요."));
        return;
      }
      settled = true;
      cleanup();
      resolve(xhrResponse(xhr));
    };
    xhr.onerror = () => rejectOnce(new Error("네트워크 연결을 확인해주세요."));
    xhr.onabort = () =>
      rejectOnce(new DOMException("업로드가 취소되었습니다.", "AbortError"));
    options.signal?.addEventListener("abort", handleSignalAbort, {
      once: true,
    });
    options.onProgress?.({ loaded: 0, total: 0, percent: 0 });
    xhr.send(body);
  });
}

const RETRYABLE_RESPONSE_STATUSES = new Set([502, 503, 504]);

function abortError() {
  return new DOMException("요청이 취소되었습니다.", "AbortError");
}

function waitForRetry(delayMs: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);
    const handleAbort = () => {
      window.clearTimeout(timeout);
      signal?.removeEventListener("abort", handleAbort);
      reject(abortError());
    };
    signal?.addEventListener("abort", handleAbort, { once: true });
  });
}

async function fetchWithSafeNetworkRetry(
  url: string,
  init: RequestInit,
  retryCount: number,
): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const safeToRetry = method === "GET" || method === "HEAD";
  const attempts = safeToRetry ? Math.max(0, Math.min(retryCount, 2)) + 1 : 1;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await fetch(url, init);
      if (
        attempt < attempts - 1
        && RETRYABLE_RESPONSE_STATUSES.has(response.status)
      ) {
        void response.body?.cancel().catch(() => undefined);
        await waitForRetry(
          250 * (2 ** attempt) + Math.round(Math.random() * 150),
          init.signal ?? undefined,
        );
        continue;
      }
      return response;
    } catch (error) {
      if (
        init.signal?.aborted
        || (error instanceof DOMException && error.name === "AbortError")
        || attempt >= attempts - 1
      ) {
        throw error;
      }
      await waitForRetry(
        250 * (2 ** attempt) + Math.round(Math.random() * 150),
        init.signal ?? undefined,
      );
    }
  }

  throw new Error("네트워크 요청을 완료하지 못했습니다.");
}

/**
 * fetch에는 업로드 진행률 이벤트가 없어, 파일 전송에 한해서 XHR을 사용한다.
 * 인증/refresh/로그인 이동 규칙은 fetchWithAuth와 동일하게 유지한다.
 */
export async function uploadWithAuthProgress(
  url: string,
  body: XMLHttpRequestBodyInit,
  options: UploadWithAuthProgressOptions = {},
): Promise<Response> {
  await waitForAuthSessionResumeGate();
  const token = getAccessToken();
  const response = await sendAuthenticatedUpload(url, body, token, options);
  if (response.status !== 401) return response;

  const latestToken = getAccessToken();
  let refreshedToken: string | undefined;
  let refreshStatus: AccessTokenRefreshResult["status"] = "authenticated";
  if (latestToken && latestToken !== token) {
    refreshedToken = latestToken;
  } else {
    const refreshResult = await refreshAccessTokenResult();
    refreshStatus = refreshResult.status;
    if (refreshResult.status === "authenticated") {
      refreshedToken = refreshResult.token;
    }
  }

  if (refreshedToken) {
    return sendAuthenticatedUpload(url, body, refreshedToken, options);
  }

  // 서버/네트워크 일시 장애를 세션 만료로 오인해 사용자를 내보내지 않는다.
  if (
    refreshStatus === "unauthenticated"
    && !options.skipRedirectOn401
    && typeof window !== "undefined"
  ) {
    window.location.href = `/login?redirect=${encodeURIComponent(getCurrentPathWithSearch())}`;
  }
  return response;
}

/**
 * 401 응답 시 refresh token으로 자동 갱신 후 재시도.
 * refresh가 인증 거부(401/403)인 경우에만 로그인 페이지로 리다이렉트한다.
 * 네트워크/서버 장애는 세션 만료로 오인하지 않는다.
 */
export async function fetchWithAuth(
  url: string,
  options: FetchWithAuthOptions = {},
): Promise<Response> {
  await waitForAuthSessionResumeGate();
  const {
    skipRedirectOn401 = false,
    networkRetryCount = 2,
    ...requestOptions
  } = options;
  const token = getAccessToken();
  const headers = withDeviceHeader(requestOptions.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetchWithSafeNetworkRetry(url, {
    ...requestOptions,
    headers,
    credentials: requestOptions.credentials ?? "include",
  }, networkRetryCount);

  if (res.status !== 401) return res;

  // 다른 요청이 먼저 refresh를 마쳤다면 다시 refresh하지 않고 최신 토큰을
  // 사용한다. 동시에 발생한 refresh 요청은 refreshAccessTokenResult에서 공유된다.
  const latestToken = getAccessToken();
  let refreshedToken: string | undefined;
  let refreshStatus: AccessTokenRefreshResult["status"] = "authenticated";
  if (latestToken && latestToken !== token) {
    refreshedToken = latestToken;
  } else {
    const refreshResult = await refreshAccessTokenResult();
    refreshStatus = refreshResult.status;
    if (refreshResult.status === "authenticated") {
      refreshedToken = refreshResult.token;
    }
  }

  if (refreshedToken) {
    // 갱신된 토큰으로 원래 요청 재시도
    const retryHeaders = withDeviceHeader(requestOptions.headers);
    retryHeaders.set("Authorization", `Bearer ${refreshedToken}`);

    return fetchWithSafeNetworkRetry(url, {
      ...requestOptions,
      headers: retryHeaders,
      credentials: requestOptions.credentials ?? "include",
    }, networkRetryCount);
  }

  // refresh endpoint가 명시적으로 세션을 거부한 경우에만 로그인으로 보낸다.
  // 5xx/네트워크 실패는 기존 화면에서 복구할 수 있도록 redirect하지 않는다.
  if (
    refreshStatus === "unauthenticated"
    && !skipRedirectOn401
    && typeof window !== "undefined"
  ) {
    window.location.href = `/login?redirect=${encodeURIComponent(getCurrentPathWithSearch())}`;
  }
  return res; // redirect 전 fallback (또는 redirect 생략/일시 장애 시 401 원본 res)
}

export function getAuthHeaders(
  extra: Record<string, string> = {},
): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = { ...extra };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}
